import { Component, type ReactNode, type ErrorInfo } from 'react'

interface Props {
    children: ReactNode
}

interface State {
    hasError: boolean
    error: Error | null
    errorInfo: ErrorInfo | null
}

export class ErrorBoundary extends Component<Props, State> {
    constructor(props: Props) {
        super(props)
        this.state = { hasError: false, error: null, errorInfo: null }
    }

    static getDerivedStateFromError(error: Error): State {
        return { hasError: true, error, errorInfo: null }
    }

    componentDidCatch(error: Error, errorInfo: ErrorInfo) {
        console.error('ErrorBoundary caught an error:', error, errorInfo)
        this.setState({ error, errorInfo })
    }

    render() {
        if (this.state.hasError) {
            return (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    zIndex: 9999,
                    backgroundColor: 'rgba(0, 0, 0, 0.95)',
                    color: '#fff',
                    padding: '2rem',
                    overflow: 'auto',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center'
                }}>
                    <div style={{
                        maxWidth: '800px',
                        background: '#1a1a1a',
                        border: '2px solid #ef4444',
                        borderRadius: '0.5rem',
                        padding: '2rem',
                        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)'
                    }}>
                        <h2 className="text-2xl font-bold text-red-500 mb-4">CRITICAL APPLICATION ERROR</h2>
                        <p className="mb-4">The application has crashed. Please send a screenshot of this screen to the developer.</p>

                        {this.state.error && (
                            <div className="mb-6">
                                <h3 className="text-lg font-semibold text-gray-300 mb-2">Error Message:</h3>
                                <pre className="bg-black p-4 rounded text-red-300 overflow-x-auto whitespace-pre-wrap font-mono text-sm border border-red-900">
                                    {this.state.error.toString()}
                                </pre>
                            </div>
                        )}

                        {this.state.errorInfo && (
                            <div className="mb-6">
                                <h3 className="text-lg font-semibold text-gray-300 mb-2">Component Stack:</h3>
                                <pre className="bg-black p-4 rounded text-gray-400 overflow-x-auto whitespace-pre-wrap font-mono text-xs border border-gray-800" style={{ maxHeight: '300px' }}>
                                    {this.state.errorInfo.componentStack}
                                </pre>
                            </div>
                        )}

                        <div className="flex gap-4 justify-end">
                            <button
                                onClick={() => window.location.reload()}
                                className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded font-medium transition-colors"
                            >
                                Reload Page
                            </button>
                        </div>
                    </div>
                </div>
            )
        }

        return this.props.children
    }
}
